import React, { createContext, useContext, useState, useCallback } from 'react';
import { api } from '../services/api.js';

/**
 * Competitive-intelligence monitoring state (plan doc §5, §8).
 *
 * Holds the user's monitors, the currently-selected monitor's feed, and the read/feedback
 * actions. Monitoring is Premium-exclusive; `hasMonitoring` (from /access) decides whether
 * the page shows the feed or an upgrade prompt (§9). The feed itself is served by the API as
 * hits joined with their signals — the client never touches the signals/entities collections.
 */

const MonitorContext = createContext();

export const useMonitor = () => {
  const context = useContext(MonitorContext);
  if (!context) {
    throw new Error('useMonitor must be used within a MonitorProvider');
  }
  return context;
};

export const MonitorProvider = ({ children }) => {
  const [access, setAccess] = useState(null); // null = unknown, true/false once probed
  const [monitors, setMonitors] = useState([]);
  const [activeMonitorId, setActiveMonitorId] = useState(null);
  const [lastSweepAt, setLastSweepAt] = useState(null);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ threat: 0, opportunity: 0, context: 0 });
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [recordsChecked, setRecordsChecked] = useState(null);
  const [feedState, setFeedState] = useState(null);
  const [labelFilter, setLabelFilter] = useState(null); // null = all
  const [loading, setLoading] = useState(false);
  /**
   * Whether the first load has finished, successfully or not.
   *
   * `loading` cannot answer this: it starts false, so on the very first render the page sees
   * "not loading, no monitors" and cannot tell that apart from "loaded, and there are none".
   * Anything that acts on emptiness — the redirect to setup — has to wait for this instead.
   */
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);

  /** Probe entitlement + load monitors. Safe to call on mount every time. */
  const initialize = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const probe = await api.getMonitoringAccess();
      setAccess(!!probe.hasMonitoring);
      if (!probe.hasMonitoring) {
        setMonitors([]);
        return;
      }
      const res = await api.getMonitors();
      const list = res.monitors || [];
      setMonitors(list);
      setLastSweepAt(res.lastSweepAt || null);
      if (list.length > 0) {
        setActiveMonitorId((prev) => prev || list[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load monitoring');
    } finally {
      setLoading(false);
      // Set even on failure: a load that errored is still a load that happened, and leaving
      // this false would strand the caller waiting for a state that is not coming.
      setInitialized(true);
    }
  }, []);

  /** Load the feed for a monitor (defaults to the active one), applying the label filter. */
  const loadFeed = useCallback(
    async (monitorId, label = labelFilter) => {
      const id = monitorId || activeMonitorId;
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.getMonitorFeed(id, label || undefined);
        const items = res.items || [];
        setItems(items);
        setCounts(res.counts || { threat: 0, opportunity: 0, context: 0 });
        setUnreadTotal(res.unreadTotal ?? 0);
        setRecordsChecked(res.recordsChecked ?? null);
        setFeedState(res.state || null);
        // Returned as well as set, so adding a competitor can count what the watch brought in
        // rather than only asserting that it worked.
        return items;
      } catch (err) {
        setError(err.message || 'Failed to load feed');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [activeMonitorId, labelFilter],
  );

  const selectMonitor = useCallback(
    (id) => {
      setActiveMonitorId(id);
      setItems([]);
      loadFeed(id);
    },
    [loadFeed],
  );

  const applyFilter = useCallback(
    (label) => {
      setLabelFilter(label);
      loadFeed(activeMonitorId, label);
    },
    [activeMonitorId, loadFeed],
  );

  /** Optimistically mark a hit read/unread. */
  const setRead = useCallback(
    async (signalId, read) => {
      if (!activeMonitorId) return;
      const target = items.find((it) => it.signalId === signalId);
      setItems((prev) => prev.map((it) => (it.signalId === signalId ? { ...it, read } : it)));
      // The chips count unread items, so they have to move with the card rather than wait
      // for the next load — otherwise "Threats 2" sits over a feed with one threat left.
      if (target && target.read !== read) {
        const delta = read ? -1 : 1;
        setCounts((c) => ({ ...c, [target.label]: Math.max(0, (c[target.label] || 0) + delta) }));
        setUnreadTotal((n) => Math.max(0, n + delta));
      }
      try {
        await api.updateMonitorHit(activeMonitorId, signalId, { read });
      } catch {
        // Roll back the card AND the chips, or the counts drift from what's on screen.
        setItems((prev) => prev.map((it) => (it.signalId === signalId ? { ...it, read: !read } : it)));
        if (target && target.read !== read) {
          const undo = read ? 1 : -1;
          setCounts((c) => ({ ...c, [target.label]: Math.max(0, (c[target.label] || 0) + undo) }));
          setUnreadTotal((n) => Math.max(0, n + undo));
        }
      }
    },
    [activeMonitorId, items],
  );

  /**
   * Keep an item for later. Independent of read — an item can be dealt with and still kept.
   *
   * No chip counts to move: saved is not one of the label buckets, so unlike read it does not
   * change what the filter row is counting.
   */
  const setSaved = useCallback(
    async (signalId, saved) => {
      if (!activeMonitorId) return;
      setItems((prev) => prev.map((it) => (it.signalId === signalId ? { ...it, saved } : it)));
      try {
        await api.updateMonitorHit(activeMonitorId, signalId, { saved });
      } catch {
        setItems((prev) => prev.map((it) => (it.signalId === signalId ? { ...it, saved: !saved } : it)));
      }
    },
    [activeMonitorId],
  );

  /** Thumbs up/down. Toggling the same value clears it. Down-votes are the eval set (§4). */
  const setFeedback = useCallback(
    async (signalId, feedback) => {
      if (!activeMonitorId) return;
      let next = feedback;
      setItems((prev) =>
        prev.map((it) => {
          if (it.signalId !== signalId) return it;
          next = it.feedback === feedback ? null : feedback;
          return { ...it, feedback: next };
        }),
      );
      try {
        await api.updateMonitorHit(activeMonitorId, signalId, { feedback: next });
      } catch {
        loadFeed();
      }
    },
    [activeMonitorId, loadFeed],
  );

  /**
   * "That is not them." Correct the company behind a watched row.
   *
   * The server decides which mistake it was: a site belonging to a company we already hold
   * moves the watch there, anything else corrects this company's domain. Either way the row
   * that comes back is the one to render, so the panel is updated from the response rather
   * than from what the user typed.
   */
  const correctCompetitor = useCallback(
    async (entityId, website) => {
      if (!activeMonitorId) return null;
      const res = await api.correctMonitorEntity(activeMonitorId, entityId, website);
      const next = { id: res.entityId, name: res.name, domain: res.domain ?? null };
      setMonitors((prev) =>
        prev.map((m) => {
          if (m.id !== activeMonitorId) return m;
          const ids = (m.entityIds || []).map((x) => (x === entityId ? next.id : x));
          return {
            ...m,
            entityIds: [...new Set(ids)],
            watchedEntities: (m.watchedEntities || [])
              .map((e) => (e.id === entityId ? next : e))
              // A switch can land on a company this agent already watched, which would
              // otherwise leave the same row twice.
              .filter((e, i, all) => all.findIndex((x) => x.id === e.id) === i),
          };
        }),
      );
      return next;
    },
    [activeMonitorId],
  );

  /**
   * Start watching a company — from the header form, or from a card's "Watch this company".
   *
   * Reflects the new watch locally rather than refetching: the feed is a 90s-timeout request
   * and nothing about it would change anyway, because matching happens at ingest. Existing
   * items keep their `company` object with `watched: true` so the card can confirm in place;
   * on the next load the server stops sending `company` for them entirely.
   */
  const addCompetitor = useCallback(
    async (name, website) => {
      if (!activeMonitorId) return null;
      const res = await api.addMonitorEntity(activeMonitorId, name, website);
      const entityId = res?.entityId || null;
      if (!entityId) return null;

      /*
       * Both lists, not just entityIds.
       *
       * A monitor carries the ids it watches AND `watchedEntities`, the resolved rows the
       * settings panel renders. Only entityIds was updated here, so adding a competitor
       * appeared to do nothing: the request succeeded, the form closed, and the row did not
       * arrive until the next full load. Removing updated both, which is why it worked and
       * adding did not.
       *
       * The name and domain come from the response rather than from what was typed, so the
       * row shows the company we resolved and the site that answered for it.
       */
      const row = { id: entityId, name: res?.name || name, domain: res?.domain ?? null };
      setMonitors((prev) =>
        prev.map((m) => {
          if (m.id !== activeMonitorId || (m.entityIds || []).includes(entityId)) return m;
          return {
            ...m,
            entityIds: [...(m.entityIds || []), entityId],
            watchedEntities: [...(m.watchedEntities || []), row],
          };
        }),
      );
      // Every card from this company, not just the one clicked.
      setItems((prev) =>
        prev.map((it) =>
          it.company?.id === entityId ? { ...it, company: { ...it.company, watched: true } } : it,
        ),
      );

      /*
       * Pull the feed again, because there is very likely something new in it.
       *
       * Adding the first watcher of a company runs a bounded backfill of their last year
       * before the request returns, and that writes hits. Without this the alerts existed the
       * moment the button finished and stayed invisible until the next reload, which reads as
       * the watch having done nothing.
       *
       * Awaited, where it used to be fired and forgotten. The caller wants to say what the
       * watch actually produced, and "12 items from Meta" is a different claim from "added" —
       * one is evidence, the other is a promise. A failed reload is not a failed watch, so it
       * degrades to a null count rather than an error.
       */
      const feed = await loadFeed(activeMonitorId).catch(() => null);
      const inFeed = feed ? feed.filter((it) => it.company?.id === entityId).length : null;

      return { ...row, inFeed };
    },
    [activeMonitorId, loadFeed],
  );

  /**
   * Stop watching a company from the settings panel's list (design screen 08).
   *
   * Mirrors addCompetitor's local reflection rather than refetching, for the same reason:
   * matching happened at ingest, so removing a watch changes nothing already in the feed.
   */
  const removeCompetitor = useCallback(
    async (entityId) => {
      if (!activeMonitorId) return;
      await api.removeMonitorEntity(activeMonitorId, entityId);
      setMonitors((prev) =>
        prev.map((m) =>
          m.id === activeMonitorId
            ? {
                ...m,
                entityIds: (m.entityIds || []).filter((id) => id !== entityId),
                watchedEntities: (m.watchedEntities || []).filter((e) => e.id !== entityId),
              }
            : m,
        ),
      );
    },
    [activeMonitorId],
  );

  /** Delete the active agent, and fall back to whichever one remains. */
  const deleteAgent = useCallback(async () => {
    if (!activeMonitorId) return;
    await api.deleteMonitor(activeMonitorId);
    setMonitors((prev) => {
      const next = prev.filter((m) => m.id !== activeMonitorId);
      setActiveMonitorId(next[0]?.id || null);
      return next;
    });
    setItems([]);
  }, [activeMonitorId]);

  /** "Try again now" on the failed state. Reloads the feed on success. */
  const retrySweep = useCallback(async () => {
    await api.retrySweep();
    await loadFeed();
  }, [loadFeed]);

  /** Update mutable monitor settings (slackWebhookUrl, digestCadence, label) + reflect locally. */
  const updateSettings = useCallback(
    async (patch) => {
      if (!activeMonitorId) return;
      await api.updateMonitor(activeMonitorId, patch);
      setMonitors((prev) => prev.map((m) => (m.id === activeMonitorId ? { ...m, ...patch } : m)));
    },
    [activeMonitorId],
  );

  const value = {
    access,
    monitors,
    activeMonitorId,
    activeMonitor: monitors.find((m) => m.id === activeMonitorId) || null,
    items,
    counts,
    unreadTotal,
    recordsChecked,
    feedState,
    retrySweep,
    labelFilter,
    loading,
    initialized,
    error,
    initialize,
    loadFeed,
    selectMonitor,
    applyFilter,
    setRead,
    setSaved,
    correctCompetitor,
    setFeedback,
    addCompetitor,
    removeCompetitor,
    deleteAgent,
    lastSweepAt,
    updateSettings,
  };

  return <MonitorContext.Provider value={value}>{children}</MonitorContext.Provider>;
};
